/**
 * Pricing Plans and Limits
 * Defines the capabilities and limits for each user tier.
 * Based on PricingPageComplete.js structure.
 */

export const PLAN_TYPES = {
    FREE_STARTER: 'free_starter',
    STUDENT_BASIC: 'student_basic',
    STUDENT_PLUS: 'student_plus',
    PRO_CREATOR: 'pro_creator',
    TEAM: 'team',
    ENTERPRISE: 'enterprise'
};

export const PRICING_PLANS = {
    [PLAN_TYPES.FREE_STARTER]: {
        name: 'Free Starter',
        maxRowsPerGeneration: 500,
        maxPointsPerMonth: 10000,
        generationsPerDay: 3,
        features: {
            webSearch: true,
            fileUploadLimitMB: 512, // 512KB
            multiModal: false,
            apiAccess: false
        }
    },
    [PLAN_TYPES.STUDENT_BASIC]: {
        name: 'Student Basic',
        maxRowsPerGeneration: 500,
        maxPointsPerMonth: 10000,
        generationsPerDay: 3,
        features: {
            webSearch: true,
            fileUploadLimitMB: 0.512, // 512KB
            multiModal: false,
            apiAccess: false
        }
    },
    [PLAN_TYPES.STUDENT_PLUS]: {
        name: 'Student Plus',
        maxRowsPerGeneration: 1000,
        maxPointsPerMonth: 25000,
        generationsPerDay: Infinity,
        features: {
            webSearch: true,
            fileUploadLimitMB: 2, // 2MB
            multiModal: false,
            apiAccess: false
        }
    },
    [PLAN_TYPES.PRO_CREATOR]: {
        name: 'Pro Creator',
        maxRowsPerGeneration: 1000,
        maxPointsPerMonth: 200000,
        generationsPerDay: Infinity,
        features: {
            webSearch: true,
            fileUploadLimitMB: 10, // 10MB
            multiModal: false,
            apiAccess: true
        }
    },
    [PLAN_TYPES.TEAM]: {
        name: 'Team',
        maxRowsPerGeneration: 5000,
        maxPointsPerMonth: 1000000,
        generationsPerDay: Infinity,
        features: {
            webSearch: true,
            fileUploadLimitMB: 100, // 100MB
            multiModal: true,
            apiAccess: true
        }
    },
    [PLAN_TYPES.ENTERPRISE]: {
        name: 'Enterprise',
        maxRowsPerGeneration: 100000,
        maxPointsPerMonth: Infinity,
        generationsPerDay: Infinity,
        features: {
            webSearch: true,
            fileUploadLimitMB: 1024, // 1GB
            multiModal: true,
            apiAccess: true
        }
    }
};

export const DEFAULT_PLAN = PLAN_TYPES.FREE_STARTER;

/**
 * Get accurate plan details based on a plan string
 * @param {string} planId - The plan identifier or name (e.g. from userDetails.plan)
 * @returns {Object} Plan details object
 */
export const getPlanDetails = (planId) => {
    if (!planId) return PRICING_PLANS[DEFAULT_PLAN];

    // Normalize input
    const normalized = String(planId).toLowerCase().trim().replace(/\s+/g, '_');

    // Direct match
    if (PRICING_PLANS[normalized]) {
        return PRICING_PLANS[normalized];
    }

    // Heuristic mapping
    if (normalized.includes('student') && normalized.includes('plus')) return PRICING_PLANS[PLAN_TYPES.STUDENT_PLUS];
    if (normalized.includes('student')) return PRICING_PLANS[PLAN_TYPES.STUDENT_BASIC];
    if (normalized.includes('pro')) return PRICING_PLANS[PLAN_TYPES.PRO_CREATOR];
    if (normalized.includes('team')) return PRICING_PLANS[PLAN_TYPES.TEAM];
    if (normalized.includes('enterprise')) return PRICING_PLANS[PLAN_TYPES.ENTERPRISE];
    if (normalized.includes('free')) return PRICING_PLANS[PLAN_TYPES.FREE_STARTER];

    return PRICING_PLANS[DEFAULT_PLAN];
};
